<template>
  <div class="max-w-lg w-full mx-auto">

    <!-- Error detected with file upload -->
    <div v-if="isFileUploadModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg w-96">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="text-xl font-bold text-red-600">Warning: We've detected an issue</h2>
        </div>
        <p class="mt-4 text-lg">{{ fileUploadMessage }}</p>
        <div class="mt-6 flex justify-between">
          <button @click="acceptError(setSourceFileToNull)" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Accept error
          </button>
        </div>
      </div>
    </div>

    <!-- Show All ceremonies warning modal -->
    <div v-if="showAll && isWarningModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg w-96">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="text-xl font-bold text-red-600">Warning: Potentially Dangerous Action</h2>
        </div>
        <p class="mt-4 text-lg">You are about view ceremonies that have already been uploaded. Please carefully select the ceremony you wish to upload and proceed with <b>caution</b> or cancel.</p>
        <div class="mt-6 flex justify-between">
          <button @click="showAllCeremonies(false)" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Cancel
          </button>
          <button @click="closeWarningModal" class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700">
            Proceed
          </button>
        </div>
      </div>
    </div>

    <!-- Show All ceremony overwrite modal -->
    <div v-if="isCeremonyOverwriteModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg md:w-2/4 w-96 ">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="md:text-2xl text-xl font-bold text-red-600">Warning: Potentially Dangerous Action</h2>
        </div>
        <p class="mt-4 text-lg">You are about to upload footage of a ceremony that may already have existing footage. To continue, 
          please verify that both the ceremony and the file are correct, then enter 
          <span style="user-select: none;">'Permanently Overwrite'</span> and click Overwrite. If you wish to cancel, select Cancel.
        </p>
        <p class="mt-4 text-md"> Ceremony:
          <b>
            {{ formatCeremonyOption(ceremonies.find(x => x.id == selected_ceremony) || null) }}
          </b>
        </p>
        <p class="my-2 text-md">File: <b>{{ source_file.name }}</b></p>
        <div class="flex flex-col">
          <input 
            type="text" 
            class="p-2 my-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
            placeholder="Permanently Overwrite"
            v-model="validateString"
          >
        </div>
        <div class="mt-6 flex justify-between">
          <button @click="isCeremonyOverwriteModalVisible = false" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Cancel
          </button>
          <button 
            id="overwriteModalSubmitBtn"
            @click="successOverwriteValidation" 
            :disabled="!isStringOverwriteValid"
            class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-75"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>

    <div v-if="completed" class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
      <div class="mt-16 mx-auto max-w-7xl px-4 sm:mt-24 sm:px-6">
        <div class="text-center">
          <h1
            class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl"
          >
            <span class="block">Upload Completed</span>
          </h1>
          <p
            class="mt-3 max-w-md mx-auto text-base text-gray-700 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
          >
            You uploaded a source file for {{ selected_university["name"] }}
            {{ formatCeremonyOption(ceremonies.find(x => x.id == selected_ceremony) || null) }}
          </p>
        </div>
      </div>
    </div>

    <form class="space-y-4 text-gray-700" @submit.prevent v-if="!completed">
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="mt-8 mx-auto max-w-7xl px-4 sm:mt-4 sm:px-6">
          <div class="text-center">
            <h1
              class="text-2xl tracking-tight font-extrabold text-gray-900 sm:text-2xl md:text-2xl"
            >
              <span class="block text-mygrad">Upload Source File</span>
            </h1>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <button
            v-if="!showAll"
            type="submit"
            @click="showAllCeremonies(true)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Show All Ceremonies (warning this will include ceremonies you have
            already uploaded)
          </button>
          <button
            v-else
            type="submit"
            @click="showAllCeremonies(false)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Hide already uploaded ceremonies (Normal mode)
          </button>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 0 && universities.length > 0">
            Select University
          </h2>
          <select
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            v-model="selected_university"
            @change="onUniChange"
            v-if="stage >= 0 && universities.length > 0"
          >
            <option disabled :value="null">Select University</option>
            <option
              v-for="(uni, c_index) in this.universities"
              :key="c_index"
              :value="uni"
            >
              {{ uni["name"] }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 1">Now Select Ceremony</h2>
          <select
            v-if="stage >= 1"
            v-model="selected_ceremony"
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
          >
            <option disabled :value="null">Select your event date</option>

            <option
              v-for="ceremony in ceremonies"
              :value="ceremony['id']"
              :key="ceremony['id']"
            >
              {{ formatCeremonyOption(ceremony) }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <input
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            type="file"
            name="file"
            ref="file"
            accept="video/mp4"
            @change="onFileChange"
            v-if="selected_ceremony != null"
          />
          <p class="mt-2 text-sm text-center text-gray-700">
            Or paste a YouTube URL - only use one option
          </p>
          <input
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            type="text"
            name="url"
            placeholder="Enter URL"
            v-model="youtube_url"
            v-if="selected_ceremony != null"
            @change="onUrlChange"
          />
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <video-player
            v-if="source_file || youtube_url"
            :options="videoOptions"
            ref="videoPlayer"
          ></video-player>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="source_file != null || youtube_url != null"
      >
        <div class="w-full px-2">
          <h3>
            Now please go to a reference frame for the first walk shot, press
            pause and then press Create First Reference
          </h3>
          <button
            type="submit"
            @click="createReference('wide')"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create First Reference
          </button>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="references.wide != null"
      >
        <div class="w-full px-2">
          <h3>
            Now please go to a reference frame for the SECOND walk shot, press
            pause and then press Create SECOND Reference
          </h3>
          <button
            type="submit"
            @click="createReference('close')"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create Second Reference
          </button>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <button
            type="submit"
            @click="checkForOverwrite"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            v-if="references.wide != null && references.close != null"
            :disabled="isUploading"
          >
            Submit
          </button>
          <div
            class="w-full bg-gray-200 rounded-full dark:bg-gray-700"
            v-if="stage > 50 || stage == -1"
          >
            <div
              class="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
              :style="'width: ' + progress + '%'"
            >
              {{ progress }}%
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>


<script>
import axios from "axios";
import "videojs-contrib-hls";

export default {
  name: "Upload",
  data: function () {
    return {
      youtube_url: null,
      stage: 0,
      isUploading: false,
      progress: false,
      universities: [],
      selected_university: null,
      ceremonies: [],
      selected_ceremony: null,
      source_file: null,
      references: {
        wide: null,
        close: null,
      },
      completed: false,
      showAll: false,
      isWarningModalVisible: true,
      isCeremonyOverwriteModalVisible: false,
      validateString: '',
      fileUploadMessage: '',
      isFileUploadModalVisible: false,
      setSourceFileToNull: false,
    };
  },
  computed: {
    videoOptions() {
      if (!this.source_file) {
        return {
          techOrder: ["youtube"],
          sources: [
            {
              type: "video/youtube",
              src: this.youtube_url,
            },
          ],
          aspectRation: "16:9",
          width: "100%",
          responsive: true,
          youtube: {
            ytControls: 2,
          },
        };
      }
      var URL = window.URL || window.webkitURL;
      var fileURL = URL.createObjectURL(this.source_file);

      return {
        sources: [
          {
            type: "video/mp4",
            src: fileURL,
          },
        ],
        aspectRation: "16:9",
        responsive: true,
      };
    },
    player() {
      return this.$refs.videoPlayer.player;
    },
    isStringOverwriteValid() {
      return this.validateString === 'Permanently Overwrite'
    }
  },
  beforeCreate() {
    axios
      .get(`/universities`)
      .then((response) => response.data)
      .then((response_data) => (this.universities = response_data))
      .catch(() => this.$router.push("/admin/login"));
  },
  mounted() {
    var showAll = this.$route.query.showAll;
    this.showAll = showAll == "true";
  },
  methods: {
    validateCeremonyOverwrite() {
      this.validateString = ''
      this.isCeremonyOverwriteModalVisible = true
    },
    closeWarningModal() {
      this.isWarningModalVisible = false;
    },
    showAllCeremonies(e) {
      this.$router.push({ path: "/admin/upload", query: { showAll: e } });
      this.$router.go();
    },
    createReference(t) {
      // Force user to scroll to a point in the video
      if (this.player.currentTime() === 0) {
        this.fileUploadMessage = "Please set an appropriate reference point.";
        this.setSourceFileToNull = false;
        this.isFileUploadModalVisible = true;
        return;
      }

      if (t == "wide") {
        // TODO: tidy reference
        this.references.wide = this.player.currentTime();
      } else {
        this.references.close = this.player.currentTime();
      }
    },

    async getSignedUrl(ceremony_id, file_name, references, type = "FILE") {
      return axios
        .post(`/ceremonies/${ceremony_id}/upload`, {
          filename: file_name,
          references: references,
          type: type
        })
        .then((response) => response.data["url"]);
    },
    async checkForOverwrite() {
      // Display modal if showAll is true
      const isOverwritePotential = this.showAll
      if(isOverwritePotential){
        return this.validateCeremonyOverwrite()
      }
      // showAll is false, submit
      return this.submitForUpload()
    },
    async successOverwriteValidation(){
      // Hide modal and submit
      this.isCeremonyOverwriteModalVisible = false 
      this.submitForUpload()
    },
    async submitForUpload() {
      if (this.youtube_url) {
        this.submitYoutubeFile();
      } else {
        this.submitSourceFile();
      }
    },
    async submitYoutubeFile() {
      this.stage = 100;
      this.isUploading = true;
      await this.getSignedUrl(
        this.selected_ceremony,
        this.youtube_url,
        this.references,
        "YOUTUBE"
      );
      this.completed = true;
    },
    async submitSourceFile() {
      this.stage = 100;
      this.isUploading = true;
      var signedUrl = await this.getSignedUrl(
        this.selected_ceremony,
        this.source_file.name,
        this.references
      );
      this.progress = 0;

      var formData = new FormData();
      for (const [key, value] of Object.entries(signedUrl.fields)) {
        formData.append(key, value);
      }
      var file = new File([this.source_file], this.source_file.name);
      formData.append("file", file);
      await axios
        .post(signedUrl.url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          transformRequest: (data, headers) => {
            delete headers.Authorization;
            delete headers.authorization;
            if (headers.common) {
              headers.common = { ...headers.common };
              delete headers.common["Authorization"];
              delete headers.common.authorization;
            }
            return data;
          },
          onUploadProgress: (progressEvent) => {
            this.stage = -1;
            let percentCompleted = Math.floor(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // do whatever you like with the percentage complete
            // maybe dispatch an action that will update a progress bar or something
            this.progress = percentCompleted;
          },
        })
        .then(() => {
          this.completed = true;
        })
        .catch(error => {
          console.error(`Error uploading file: ${error.message}`);
        })
    },

    onUniChange() {
      this.getCeremonies(this.selected_university["id"]).then(
        (ceremonies) => (this.ceremonies = ceremonies)
      );
      this.stage = 1;
      this.selected_ceremony = null;
    },
    async getCeremonies(uni_id) {
      if (typeof this.showAll != "undefined" && this.showAll) {
        return axios
          .get(`/universities/${uni_id}/ceremonies`)
          .then((response) => response.data);
      } else {
        return axios
          .get(`/universities/${uni_id}/ceremonies?ready=false`)
          .then((response) => response.data);
      }
    },
    onFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.source_file = files[0];
      this.validateFileUpload(this.source_file)
    },
    onUrlChange() {},
    async validateFileUpload(file) {
      try {
        const validSize = this.validateFileSize(file);
        if(!validSize){
          this.setSourceFileToNull = true;
          throw new Error('This file is larger than expected. Please upload a file less than 4GB.')
        }
        // File passed validation checks
        return;
      } catch (error) {
        console.error(`Error validating file: ${error.message}`);
        this.fileUploadMessage = error.message;
        this.isFileUploadModalVisible = true;
        return;
      }
    },
    validateFileSize(file){
      // Use Decimal base 10 size limit (S3)
      const maxSizeInBytes = 5 * 1000 * 1000 * 1000;
      return file.size <= maxSizeInBytes;
    },
    setSourceFile(v){
      this.source_file = v;
      this.$refs.file.value = v;
    },
    acceptError(sourceFileToNull) {
      this.isFileUploadModalVisible = false;
      if (sourceFileToNull == true)
        this.setSourceFile(null);
    },
    formatCeremonyOption(ceremony) {
      if (!ceremony) return "";
      const formattedDate = this.formatDate(ceremony.datetime);
      return ceremony.label
        ? `${formattedDate} - ${ceremony.label}`
        : formattedDate;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
  },

  components: {},
};
</script>
<style scoped>
video {
  min-width: 100%;
  min-height: 40em;
  position: relative !important;
}
.video-js {
  width: 100% !important;
  min-height: 40em !important;
  position: relative !important;
}
button#overwriteModalSubmitBtn:disabled{
  visibility: visible!important;
  background-color: grey;
  color: white;
  opacity: 0.5;
  cursor: none;
}
</style>
